import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import fitCoinService, { FitCoinBalance } from '../../services/fitcoin.service';

export interface OutfitData {
  images: string[];
  creator?: string;
  postId?: string;        // Archive post ID for archive-based try-on
  productId?: string;     // Product ID for product-based try-on
  creatorAvatar?: string;
  userId?: string;
}

export interface ReturnContext {
  type: 'product';
  productId: string;
  productName: string;
  productPrice?: number;
  currency?: string;
}

interface TryOnState {
  selectedOutfit: OutfitData | null;
  selectedImageIndex: number;
  returnContext: ReturnContext | null;
  balanceData: FitCoinBalance | null;
  isLoadingBalance: boolean;
  balanceError: string | null;
}

const initialState: TryOnState = {
  selectedOutfit: null,
  selectedImageIndex: 0,
  returnContext: null,
  balanceData: null,
  isLoadingBalance: false,
  balanceError: null,
};

// Async thunks
export const fetchFitCoinBalance = createAsyncThunk(
  'tryOn/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fitCoinService.getBalance();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch balance');
    }
  }
);

export const deductFitCoinsForTryOn = createAsyncThunk(
  'tryOn/deductForTryOn',
  async (postId: string, { rejectWithValue }) => {
    try {
      const data = await fitCoinService.deductForTryOn(postId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deduct coins');
    }
  }
);

const tryOnSlice = createSlice({
  name: 'tryOn',
  initialState,
  reducers: {
    setSelectedOutfit: (state, action: PayloadAction<OutfitData | null>) => {
      state.selectedOutfit = action.payload;
      state.selectedImageIndex = 0;
    },
    setSelectedImageIndex: (state, action: PayloadAction<number>) => {
      state.selectedImageIndex = action.payload;
    },
    clearSelectedOutfit: (state) => {
      state.selectedOutfit = null;
      state.selectedImageIndex = 0;
      state.returnContext = null;
    },
    setReturnContext: (state, action: PayloadAction<ReturnContext | null>) => {
      state.returnContext = action.payload;
    },
    clearReturnContext: (state) => {
      state.returnContext = null;
    },
    setBalanceData: (state, action: PayloadAction<FitCoinBalance>) => {
      state.balanceData = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch balance
    builder.addCase(fetchFitCoinBalance.pending, (state) => {
      state.isLoadingBalance = true;
      state.balanceError = null;
    });
    builder.addCase(fetchFitCoinBalance.fulfilled, (state, action) => {
      state.isLoadingBalance = false;
      state.balanceData = action.payload;
    });
    builder.addCase(fetchFitCoinBalance.rejected, (state, action) => {
      state.isLoadingBalance = false;
      state.balanceError = action.payload as string;
    });

    // Deduct coins
    builder.addCase(deductFitCoinsForTryOn.fulfilled, (state, action) => {
      state.balanceData = action.payload;
    });
  },
});

export const { 
  setSelectedOutfit, 
  setSelectedImageIndex, 
  clearSelectedOutfit,
  setReturnContext,
  clearReturnContext,
  setBalanceData,
} = tryOnSlice.actions;
export default tryOnSlice.reducer;
